import type { PageGroup } from '@st/sdk'
import { match } from '@st/pattern'

function unassigned(first: number, last: number): PageGroup {
  return {
    pageRange: { first, last },
    children: [],
    metadata: {},
    title: undefined,
    categoryId: undefined,
    id: undefined
  }
}

/** Fill legacy bookmark gaps without assigning them the neighboring bookmark's meaning.
 * This is an editing view; the full collection is saved only when an edit is made.
 */
export function editablePageGroups(doc: {
  pageGroups: PageGroup[]
  pageCount?: number | null
}): PageGroup[] {
  const pageCount = doc.pageCount ?? doc.pageGroups.at(-1)?.pageRange.last
  if (pageCount == null) throw new Error('Page count is required')
  const groups: PageGroup[] = []
  let nextPage = 1
  for (const group of doc.pageGroups) {
    if (group.pageRange.first > nextPage) {
      groups.push(unassigned(nextPage, group.pageRange.first - 1))
    }
    groups.push(group)
    nextPage = group.pageRange.last + 1
  }
  if (nextPage <= pageCount) {
    groups.push(unassigned(nextPage, pageCount))
  }
  return groups
}

/** Changed groups deliberately discard meaning; untouched groups retain their metadata. */
export function updatedPageGroups(
  doc: { pageGroups: PageGroup[]; pageCount?: number | null },
  action: 'add' | 'remove',
  afterPage: number
): PageGroup[] {
  const groups = match(doc.pageGroups.length)
    .with(0, () => editablePageGroups(doc))
    .otherwise(() => doc.pageGroups)
  return match(action)
    .with('add', () =>
      groups.flatMap((group) => {
        if (afterPage < group.pageRange.first || afterPage >= group.pageRange.last) return [group]
        return [
          unassigned(group.pageRange.first, afterPage),
          unassigned(afterPage + 1, group.pageRange.last)
        ]
      })
    )
    .with('remove', () => {
      const result: PageGroup[] = []
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i]
        const next = groups[i + 1]
        if (group.pageRange.last === afterPage && next?.pageRange.first === afterPage + 1) {
          result.push(unassigned(group.pageRange.first, next.pageRange.last))
          i++
        } else result.push(group)
      }
      return result
    })
    .exhaustive()
}
